import { useState, useEffect, useMemo, useRef } from "react";
import { apiCall } from "./api";
import { db, collection, doc, onSnapshot, query, orderBy, where } from "./firebase";
import RulesPanel, { RulesButton } from "./rules-panel";
import FeedbackPanel, { FeedbackButton } from "./feedback-panel";
import blackOwlLogo from "./assets/black-owl-logo.png";
import { FONT_BODY, FONT_DISPLAY, ELLIPSIS } from "./theme";
import { SpectatingBadge, leaveLobbyBody } from "./spectator";
import { notify, useMissedEventsTitle } from "./notify";
import NotificationsToggle from "./notifications-toggle";
import { describePenalty, hasHouseRules, packageOptions, UNIT_TITLES } from "./house-rules";

// ============================================================
// BLACK OWL - Game Screen
// Live arrow tracking and firing during NFL games
// ============================================================

// --- Owl icon ---
function OwlIcon() {
  return (
    <img
      src={blackOwlLogo}
      alt="Black Owl"
      style={{ display: "block", width: 60, height: "auto", objectFit: "contain" }}
    />
  );
}

// --- Background ---
function FieldBackground() {
  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 0, overflow: "hidden",
      background: "linear-gradient(165deg, #0a0a14 0%, #0f1a0f 40%, #0a0a14 100%)",
    }}>
      {Array.from({ length: 12 }).map((_, i) => (
        <div key={i} style={{
          position: "absolute", left: 0, right: 0,
          top: `${(i + 1) * 8}%`, height: "1px",
          background: `rgba(201, 162, 39, ${i === 5 ? 0.12 : 0.04})`,
        }}/>
      ))}
      <div style={{
        position: "absolute", inset: 0,
        background: "radial-gradient(ellipse at center, transparent 40%, rgba(0,0,0,0.6) 100%)",
      }}/>
    </div>
  );
}

// --- Position colors ---
const positionColors = {
  QB: { bg: "rgba(220, 80, 80, 0.15)", border: "rgba(220, 80, 80, 0.35)", text: "#dc5050" },
  RB: { bg: "rgba(74, 158, 255, 0.15)", border: "rgba(74, 158, 255, 0.35)", text: "#4a9eff" },
  WR: { bg: "rgba(50, 200, 120, 0.15)", border: "rgba(50, 200, 120, 0.35)", text: "#32c878" },
  TE: { bg: "rgba(200, 160, 50, 0.15)", border: "rgba(200, 160, 50, 0.35)", text: "#c8a032" },
  K: { bg: "rgba(180, 100, 220, 0.15)", border: "rgba(180, 100, 220, 0.35)", text: "#b464dc" },
};

// --- FB-19: stat counters as compact chips ("2 TD · 1 INT"), nonzero only.
// Ordered by position; the position's own TD type reads plain "TD". ---
const STAT_COUNTERS = ["pass_td", "rush_td", "rec_td", "ints", "fumbles", "fg_made", "fg_missed"];
const STAT_CHIP_ORDER = {
  QB: ["pass_td", "ints", "fumbles", "rush_td"],
  RB: ["rush_td", "rec_td", "fumbles"],
  WR: ["rec_td", "rush_td", "fumbles"],
  TE: ["rec_td", "rush_td", "fumbles"],
  K: ["fg_made", "fg_missed"],
};
const STAT_CHIP_LABELS = {
  pass_td: "Pass TD", rush_td: "Rush TD", rec_td: "Rec TD",
  ints: "INT", fumbles: "FUM", fg_made: "FG", fg_missed: "FG miss",
};
const PRIMARY_TD = { QB: "pass_td", RB: "rush_td", WR: "rec_td", TE: "rec_td" };
const BAD_STATS = new Set(["ints", "fumbles", "fg_missed"]);

function pickStats(info) {
  return Object.fromEntries(STAT_COUNTERS.map((k) => [k, info[k] || 0]));
}

function StatChips({ player, style }) {
  const order = STAT_CHIP_ORDER[player.position] || [];
  const keys = [...order, ...STAT_COUNTERS.filter((k) => !order.includes(k))]
    .filter((k) => player[k] > 0);
  if (keys.length === 0) return null;
  return (
    <div style={{
      fontSize: "11px", fontWeight: 600, fontFamily: FONT_BODY,
      whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
      ...style,
    }}>
      {keys.map((k, i) => (
        <span key={k}>
          {i > 0 && <span style={{ color: "#5a5a6e" }}> · </span>}
          <span style={{ color: BAD_STATS.has(k) ? "#e85555" : "#c9a227" }}>
            {player[k]} {k === PRIMARY_TD[player.position] ? "TD" : STAT_CHIP_LABELS[k]}
          </span>
        </span>
      ))}
    </div>
  );
}

function getPositionColor(pos) {
  return positionColors[pos] || { bg: "rgba(140,140,160,0.15)", border: "rgba(140,140,160,0.35)", text: "#8c8ca0" };
}

// --- Arrow icon ---
function ArrowIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* shaft */}
      <line x1="4" y1="20" x2="17" y2="7" stroke="#c9a227" strokeWidth="2" strokeLinecap="round"/>
      {/* arrowhead */}
      <path d="M13 5 L20 4 L19 11 Z" fill="#c9a227" stroke="#e0be4a" strokeWidth="1" strokeLinejoin="round"/>
      {/* fletching */}
      <path d="M4 20 L4 16 L8 16 Z" fill="#c9a227" opacity="0.85"/>
      <path d="M4 20 L8 20 L8 16 Z" fill="#e0be4a" opacity="0.6"/>
    </svg>
  );
}

// --- Live pulse dot ---
function LivePulse() {
  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
      <div style={{
        width: "8px", height: "8px", borderRadius: "50%",
        background: "#32c850",
        boxShadow: "0 0 8px rgba(50, 200, 80, 0.5)",
        animation: "pulse 2s infinite",
      }}/>
      <span style={{
        fontSize: "10px", fontWeight: 700, letterSpacing: "2px",
        textTransform: "uppercase", color: "#32c850",
        fontFamily: FONT_BODY,
      }}>LIVE</span>
    </div>
  );
}

// --- Scoreboard card (your arrow count) ---
function ArrowScoreboard({ tokens, username }) {
  return (
    <div style={{ marginBottom: "12px" }}>
      <div style={{
        padding: "20px 24px",
        background: "linear-gradient(135deg, rgba(201, 162, 39, 0.08) 0%, rgba(201, 162, 39, 0.03) 100%)",
        border: "1.5px solid rgba(201, 162, 39, 0.2)",
        borderRadius: "14px",
        display: "flex", alignItems: "center", justifyContent: "space-between",
      }}>
        <div>
          <p style={{
            fontSize: "10px", fontWeight: 600, letterSpacing: "2px",
            textTransform: "uppercase", color: "#5a5a6e",
            margin: "0 0 6px", fontFamily: FONT_BODY,
          }}>Your Arrows</p>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <ArrowIcon size={28} />
            <span style={{
              fontSize: "36px", fontWeight: 700, color: "#c9a227",
              fontFamily: FONT_BODY,
              lineHeight: 1,
            }}>{tokens}</span>
          </div>
        </div>
        <div style={{ textAlign: "right", minWidth: 0 }}>
          <p style={{
            fontSize: "10px", fontWeight: 600, letterSpacing: "2px",
            textTransform: "uppercase", color: "#5a5a6e",
            margin: "0 0 4px", fontFamily: FONT_BODY, ...ELLIPSIS,
          }}>{username}</p>
          <LivePulse />
        </div>
      </div>
    </div>
  );
}

// --- Small pay button used in the owed card ---
// Sender name the polling service uses for penalties the game hands out
const GAME_SENDER = "The Game";

function PayButton({ label, onClick, disabled, primary }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: "8px 12px",
        fontSize: "11px", fontWeight: 700,
        letterSpacing: "1px", textTransform: "uppercase",
        fontFamily: FONT_BODY,
        color: primary ? "#0a0a14" : "#32c850",
        background: primary
          ? "linear-gradient(135deg, #32c850 0%, #4ade6a 50%, #32c850 100%)"
          : "rgba(50, 200, 80, 0.08)",
        border: primary ? "none" : "1px solid rgba(50, 200, 80, 0.3)",
        borderRadius: "8px",
        cursor: disabled ? "wait" : "pointer",
        opacity: disabled ? 0.6 : 1,
        whiteSpace: "nowrap",
      }}
    >{label}</button>
  );
}

// --- "Arrows you owe" card (FB-05 / FB-06 / FB-26) ---
// Loose arrows pay with Pay 1 / Pay all / Pay N; each packaged shot
// (cannonball, meteor) is its own row, paid whole with Done (D8).
// Loose arrows are never shown as packages (D6).
function OweCard({ penalties, penaltiesFrom, items, houseRules, onPay, onPayItem, onPayAll, paying }) {
  const [customAmount, setCustomAmount] = useState(1);
  const packagedBySender = {};
  (items || []).forEach((it) => { packagedBySender[it.from] = (packagedBySender[it.from] || 0) + it.arrows; });
  const senders = Object.entries(penaltiesFrom || {})
    .filter(([, count]) => count > 0)
    .sort((a, b) => b[1] - a[1]);
  const attributed = senders.reduce((sum, [, count]) => sum + count, 0);
  const unattributed = Math.max(penalties - attributed, 0);
  const packagedTotal = (items || []).reduce((sum, it) => sum + it.arrows, 0);
  const looseTotal = Math.max(penalties - packagedTotal, 0);
  const amount = Math.max(Math.min(customAmount, looseTotal), 1);
  // Loose arrows, The Game's included, read with the arrow action (D10)
  const looseLine = (n) => {
    const { what, serve } = describePenalty(houseRules, "arrow", n, n);
    return serve ? `${what} · ${serve}` : what;
  };

  return (
    <div style={{
      padding: "16px 18px", marginBottom: "12px",
      background: "linear-gradient(135deg, rgba(220, 50, 50, 0.10) 0%, rgba(220, 50, 50, 0.04) 100%)",
      border: "1.5px solid rgba(220, 50, 50, 0.3)",
      borderRadius: "14px",
    }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: "12px" }}>
        <p style={{
          fontSize: "12px", fontWeight: 700, letterSpacing: "2px",
          textTransform: "uppercase", color: "#e85555",
          margin: 0, fontFamily: FONT_BODY,
        }}>Arrows you owe</p>
        <span style={{
          fontSize: "28px", fontWeight: 700, color: "#e85555",
          fontFamily: FONT_BODY, lineHeight: 1,
        }}>{penalties}</span>
      </div>

      {senders.map(([sender, count]) => {
        const loose = Math.max(count - (packagedBySender[sender] || 0), 0);
        const senderItems = (items || []).filter((it) => it.from === sender);
        return (
          <div key={sender}>
            {loose > 0 && (
              <div style={{
                display: "flex", alignItems: "center", gap: "8px",
                padding: "10px 12px", marginBottom: "6px",
                background: "rgba(0,0,0,0.2)", borderRadius: "8px",
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{
                    fontSize: "14px", fontWeight: 600, color: "#e8e6df", margin: 0,
                    fontFamily: FONT_BODY, ...ELLIPSIS,
                  }}>{sender}</p>
                  <p style={{ fontSize: "11px", color: "#a0a0b0", margin: "2px 0 0", fontFamily: FONT_BODY, ...ELLIPSIS }}>
                    {looseLine(loose)}
                  </p>
                </div>
                <PayButton label="Pay 1" disabled={paying} onClick={() => onPay(1, sender)} />
                {loose > 1 && (
                  <PayButton label="Pay all" primary disabled={paying} onClick={() => onPay(loose, sender)} />
                )}
              </div>
            )}
            {senderItems.map((it) => {
              const { what, serve } = describePenalty(houseRules, it.unit, it.count, it.arrows,
                { action: it.actionSnapshot, plural: it.pluralSnapshot });
              return (
                <div key={it.id} style={{
                  display: "flex", alignItems: "center", gap: "8px",
                  padding: "10px 12px", marginBottom: "6px",
                  background: "rgba(201, 162, 39, 0.08)",
                  border: "1px solid rgba(201, 162, 39, 0.35)", borderRadius: "8px",
                }}>
                  <span style={{ fontSize: "16px", flexShrink: 0 }}>{it.unit === "meteor" ? "☄️" : "💣"}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{
                      fontSize: "13px", fontWeight: 700, color: "#e0be4a", margin: 0,
                      fontFamily: FONT_BODY, ...ELLIPSIS,
                    }}>{what}</p>
                    <p style={{ fontSize: "11px", color: "#a0a0b0", margin: "2px 0 0", fontFamily: FONT_BODY, ...ELLIPSIS }}>
                      from {sender}{serve && ` · ${serve}`}
                    </p>
                  </div>
                  <PayButton label="Done" primary disabled={paying} onClick={() => onPayItem(it.id)} />
                </div>
              );
            })}
          </div>
        );
      })}

      {unattributed > 0 && (
        <p style={{ fontSize: "12px", color: "#a0a0b0", margin: "0 0 6px", fontFamily: FONT_BODY }}>
          {unattributed} from earlier in the game
        </p>
      )}

      {/* Custom amount (loose arrows only, largest sender first) and pay everything */}
      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "10px" }}>
        {looseTotal > 0 && (
          <>
            <button
              onClick={() => setCustomAmount(Math.max(1, amount - 1))}
              aria-label="Pay fewer"
              style={stepperStyle}
            >−</button>
            <span style={{
              minWidth: "28px", textAlign: "center",
              fontSize: "18px", fontWeight: 700, color: "#e8e6df", fontFamily: FONT_BODY,
            }}>{amount}</span>
            <button
              onClick={() => setCustomAmount(Math.min(looseTotal, amount + 1))}
              aria-label="Pay more"
              style={stepperStyle}
            >+</button>
            <PayButton label={`Pay ${amount}`} disabled={paying} onClick={() => onPay(amount)} />
          </>
        )}
        <div style={{ flex: 1 }} />
        <PayButton label="Pay all" primary disabled={paying} onClick={onPayAll} />
      </div>
      <p style={{ fontSize: "11px", color: "#7a7a8e", margin: "10px 0 0", fontFamily: FONT_BODY }}>
        Pay once you've done the real-life penalty.
      </p>
    </div>
  );
}

const stepperStyle = {
  width: "34px", height: "34px",
  fontSize: "18px", fontWeight: 700,
  color: "#e8e6df", background: "rgba(255,255,255,0.06)",
  border: "1px solid rgba(255,255,255,0.1)",
  borderRadius: "8px", cursor: "pointer",
  display: "flex", alignItems: "center", justifyContent: "center",
  fontFamily: FONT_BODY,
};

// --- One-time hint card (FB-05) ---
function HintCard({ text, onDismiss }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: "10px",
      padding: "12px 14px", marginBottom: "12px",
      background: "rgba(74, 158, 255, 0.08)",
      border: "1px solid rgba(74, 158, 255, 0.3)",
      borderRadius: "10px",
    }}>
      <span style={{ fontSize: "16px" }}>💡</span>
      <p style={{ flex: 1, fontSize: "13px", color: "#c8d8f0", margin: 0, fontFamily: FONT_BODY }}>{text}</p>
      <button
        onClick={onDismiss}
        aria-label="Dismiss hint"
        style={{
          padding: "4px 10px", fontSize: "11px", fontWeight: 700,
          fontFamily: FONT_BODY, color: "#4a9eff",
          background: "transparent", border: "1px solid rgba(74, 158, 255, 0.4)",
          borderRadius: "6px", cursor: "pointer",
        }}
      >Got it</button>
    </div>
  );
}

// --- FB-18: one-time welcome with the starter arrows (D7: exact text) ---
const WELCOME_TEXT = "Welcome to the battle grounds, here is your starter pack of arrows to get you started. When you're ready fire away at your friends and get this party started!";

function WelcomeModal({ starterArrows, onDismiss }) {
  return (
    <div role="dialog" aria-modal="true" aria-labelledby="welcome-title" style={{
      position: "fixed", inset: 0, zIndex: 1000,
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: "24px",
      background: "rgba(5, 5, 10, 0.85)",
      backdropFilter: "blur(6px)",
    }}>
      <div style={{
        width: "100%", maxWidth: "360px",
        padding: "28px 24px", textAlign: "center",
        background: "#14120c",
        border: "2px solid rgba(201, 162, 39, 0.5)",
        borderRadius: "16px",
        boxShadow: "0 20px 80px rgba(201, 162, 39, 0.2)",
      }}>
        <h2 id="welcome-title" style={{
          fontSize: "22px", fontWeight: 700, color: "#c9a227",
          margin: "0 0 12px", fontFamily: FONT_DISPLAY,
          textTransform: "uppercase", letterSpacing: "1px",
        }}>Welcome</h2>
        <p style={{ fontSize: "14px", lineHeight: 1.5, color: "#c0c0cc", margin: "0 0 18px", fontFamily: FONT_BODY }}>
          {WELCOME_TEXT}
        </p>
        <div style={{
          display: "inline-flex", alignItems: "center", gap: "8px",
          padding: "10px 18px", marginBottom: "22px",
          background: "rgba(201, 162, 39, 0.1)", borderRadius: "10px",
        }}>
          <ArrowIcon size={22} />
          <span style={{ fontSize: "28px", fontWeight: 700, color: "#c9a227", fontFamily: FONT_BODY }}>
            {starterArrows}
          </span>
          <span style={{ fontSize: "13px", color: "#a0a0b0", fontFamily: FONT_BODY }}>
            starter arrow{starterArrows !== 1 ? "s" : ""}
          </span>
        </div>
        <button
          onClick={onDismiss}
          autoFocus
          style={{
            width: "100%", padding: "14px",
            fontSize: "15px", fontWeight: 700,
            letterSpacing: "2px", textTransform: "uppercase",
            fontFamily: FONT_BODY,
            color: "#0a0a14", background: "#c9a227",
            border: "none", borderRadius: "10px", cursor: "pointer",
          }}
        >Let's go</button>
      </div>
    </div>
  );
}

// --- FB-23: nudge to fire when arrows sit unspent (D8, in-app only) ---
const REMINDER_IDLE_MS = 20 * 60 * 1000;

function SpendReminder({ arrows, onFire, onDismiss }) {
  return (
    <div role="status" style={{
      display: "flex", alignItems: "center", gap: "10px",
      padding: "12px 14px", marginBottom: "12px",
      background: "rgba(201, 162, 39, 0.1)",
      border: "1px solid rgba(201, 162, 39, 0.4)",
      borderRadius: "10px",
    }}>
      <ArrowIcon size={18} />
      <p style={{ flex: 1, minWidth: 0, fontSize: "13px", fontWeight: 600, color: "#e8e6df", margin: 0, fontFamily: FONT_BODY }}>
        You have {arrows} arrow{arrows !== 1 ? "s" : ""} to spend.
      </p>
      <button
        onClick={onFire}
        style={{
          padding: "4px 10px", fontSize: "11px", fontWeight: 700,
          fontFamily: FONT_BODY, color: "#0a0a14",
          background: "#c9a227", border: "none",
          borderRadius: "6px", cursor: "pointer", flexShrink: 0,
        }}
      >🏹 Fire</button>
      <button
        onClick={onDismiss}
        aria-label="Dismiss reminder"
        style={{
          padding: "2px 6px", fontSize: "16px", lineHeight: 1,
          fontFamily: FONT_BODY, color: "#7a7a8e",
          background: "transparent", border: "none", cursor: "pointer", flexShrink: 0,
        }}
      >×</button>
    </div>
  );
}

// --- Blocking alert when arrows are fired at you (FB-10) ---
function PenaltyAlertModal({ alert, totalOwed, remaining, onAcknowledge, onAcknowledgeAll, houseRules }) {
  // FB-27: say what hit you; The Game only ever fires loose arrows (D10)
  const unit = alert.from === GAME_SENDER ? "arrow" : alert.unit || "arrow";
  const { what, serve } = describePenalty(houseRules, unit, alert.count, alert.amount);
  return (
    <div role="alertdialog" aria-modal="true" aria-labelledby="penalty-alert-title" style={{
      position: "fixed", inset: 0, zIndex: 1000,
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: "24px",
      background: "rgba(5, 5, 10, 0.85)",
      backdropFilter: "blur(6px)",
    }}>
      <div style={{
        width: "100%", maxWidth: "360px",
        padding: "28px 24px", textAlign: "center",
        background: "#14101a",
        border: "2px solid rgba(220, 50, 50, 0.5)",
        borderRadius: "16px",
        boxShadow: "0 20px 80px rgba(220, 50, 50, 0.25)",
      }}>
        <div style={{ fontSize: "40px", marginBottom: "8px" }}>🏹</div>
        <h2 id="penalty-alert-title" style={{
          fontSize: "24px", fontWeight: 700, color: "#e85555",
          margin: "0 0 10px", fontFamily: FONT_DISPLAY,
          textTransform: "uppercase", letterSpacing: "1px",
        }}>
          <span style={{
            ...ELLIPSIS, display: "inline-block", maxWidth: "100%", verticalAlign: "bottom",
          }}>{alert.from}</span>{" "}
          hit you with {what}
        </h2>
        <p style={{ fontSize: "14px", color: "#c0c0cc", margin: "0 0 22px", fontFamily: FONT_BODY, overflowWrap: "anywhere" }}>
          {serve && <><span style={{ fontWeight: 700, color: "#e8e6df" }}>Time to serve {serve}.</span><br /></>}
          You now owe {totalOwed} total. {serve ? "Pay it off once it's done." : "Time to pay up!"}
          {remaining > 0 && <><br />{remaining} more alert{remaining !== 1 ? "s" : ""} waiting.</>}
        </p>
        <button
          onClick={onAcknowledge}
          autoFocus
          style={{
            width: "100%", padding: "14px",
            fontSize: "15px", fontWeight: 700,
            letterSpacing: "2px", textTransform: "uppercase",
            fontFamily: FONT_BODY,
            color: "#0a0a14", background: "#e85555",
            border: "none", borderRadius: "10px", cursor: "pointer",
          }}
        >OK</button>
        {remaining > 0 && (
          <button
            onClick={onAcknowledgeAll}
            style={{
              marginTop: "10px", padding: "8px",
              fontSize: "12px", fontFamily: FONT_BODY,
              color: "#a0a0b0", background: "transparent",
              border: "none", cursor: "pointer", textDecoration: "underline",
            }}
          >Dismiss all {remaining + 1}</button>
        )}
      </div>
    </div>
  );
}

// --- Leaderboard row ---
// Tap the row to see that team's roster; tap Fire to send arrows.
// --- FB-20: lifetime arrows earned from scoring, scaled to the top earner ---
function EarnedBar({ earned, max }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
      <div style={{
        flex: 1, height: "4px", borderRadius: "2px",
        background: "rgba(255,255,255,0.06)", overflow: "hidden",
      }}>
        <div style={{
          height: "100%", borderRadius: "2px", background: "#c9a227",
          width: `${max > 0 ? (earned / max) * 100 : 0}%`,
          transition: "width 0.5s ease",
        }}/>
      </div>
      <span style={{
        fontSize: "10px", fontWeight: 600, color: "#8a8a9e",
        fontFamily: FONT_BODY, flexShrink: 0, width: "56px", textAlign: "right",
      }}>{earned} earned</span>
    </div>
  );
}

// --- FB-20: where a balance came from. Lost (INT/FUM/missed FG) is derived:
// balance = starter + earned - lost - fired. Lobbies started before these
// fields existed show only what they have. ---
function ArrowBreakdown({ player }) {
  const { startingArrows: start, totalEarned: earned, totalSpent: fired, points } = player;
  const items = [];
  if (start != null) items.push(["Starter", start, "#8a8a9e"]);
  items.push(["Earned", earned, "#c9a227"]);
  if (start != null && fired != null) items.push(["Lost", Math.max(start + earned - fired - points, 0), "#e85555"]);
  if (fired != null) items.push(["Fired", fired, "#e85555"]);
  items.push(["Left", points, "#e8e6df"]);
  items.push(["Owed", player.penalties || 0, "#e85555"]);
  return (
    <div style={{
      display: "flex", flexWrap: "wrap", gap: "4px 12px",
      padding: "0 0 8px", marginBottom: "4px",
      borderBottom: "1px solid rgba(255,255,255,0.05)",
    }}>
      {items.map(([label, value, color]) => (
        <span key={label} style={{ fontSize: "11px", fontFamily: FONT_BODY, color: "#5a5a6e" }}>
          {label} <span style={{ fontWeight: 700, color }}>{value}</span>
        </span>
      ))}
    </div>
  );
}

function LeaderboardRow({ player, rank, isYou, onSpend, maxEarned, leadText, canFire = true, houseRules, myTokens = 0 }) {
  const [showRoster, setShowRoster] = useState(false);
  const [showFire, setShowFire] = useState(false);
  const [spendAmount, setSpendAmount] = useState(1);
  // FB-25: fire loose arrows or a packaged unit from House Rules
  const [fireUnit, setFireUnit] = useState("arrow");
  const packages = packageOptions(houseRules);
  const activePackage = packages.find((p) => p.unit === fireUnit);
  const unit = activePackage ? fireUnit : "arrow";
  const worth = activePackage ? activePackage.worth : 1;
  const maxCount = Math.max(1, Math.floor(myTokens / worth));
  const fireCount = Math.min(spendAmount, maxCount);
  const firePreview = describePenalty(houseRules, unit, fireCount, fireCount * worth);
  const expanded = showRoster || showFire;

  return (
    <div style={{ marginBottom: "8px" }}>
      <div
        onClick={() => setShowRoster(!showRoster)}
        style={{
          display: "flex", alignItems: "center", columnGap: "12px", flexWrap: "wrap",
          padding: "14px 16px",
          background: isYou
            ? "rgba(201, 162, 39, 0.06)"
            : expanded
              ? "rgba(255,255,255,0.04)"
              : "rgba(255,255,255,0.02)",
          border: `1.5px solid ${isYou ? "rgba(201, 162, 39, 0.15)" : expanded ? "rgba(255,255,255,0.1)" : "rgba(255,255,255,0.05)"}`,
          borderRadius: expanded ? "10px 10px 0 0" : "10px",
          cursor: "pointer",
          transition: "all 0.2s ease",
        }}
      >
        {/* Rank */}
        <span style={{
          fontSize: "16px", fontWeight: 700,
          color: rank === 1 ? "#c9a227" : rank === 2 ? "#a0a0b0" : rank === 3 ? "#b87333" : "#3a3a4e",
          fontFamily: FONT_BODY,
          width: "28px", flexShrink: 0, textAlign: "center",
        }}>
          {rank === 1 ? "👑" : `#${rank}`}
        </span>

        {/* Team info */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", margin: "0 0 2px", height: "20px" }}>
            <p style={{
              fontSize: "15px", fontWeight: 600, color: "#e8e6df", lineHeight: "20px",
              margin: 0, fontFamily: FONT_BODY, ...ELLIPSIS,
            }}>{player.teamName}</p>
            {isYou && (
              <span style={{
                fontSize: "9px", fontWeight: 700, letterSpacing: "1.5px",
                textTransform: "uppercase", color: "#c9a227",
                fontFamily: FONT_BODY, flexShrink: 0,
                background: "rgba(201, 162, 39, 0.12)",
                padding: "2px 6px", borderRadius: "3px",
              }}>YOU</span>
            )}
          </div>
          {/* One line: "username • roster ▾"; the leader's says why they lead */}
          <p style={{
            fontSize: "11px", color: "#5a5a6e", margin: 0, lineHeight: "16px",
            fontFamily: FONT_BODY, ...ELLIPSIS,
          }}>
            {player.username !== player.teamName && `${player.username} • `}
            {leadText
              ? <span style={{ fontWeight: 600, color: "#c9a227" }}>{leadText}</span>
              : showRoster ? "hide roster ▴" : "roster ▾"}
          </p>
        </div>

        {/* Arrow count and penalties: own column, never under the name */}
        <div style={{ display: "flex", alignItems: "center", gap: "10px", flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <ArrowIcon size={18} />
            <span style={{
              fontSize: "20px", fontWeight: 700, color: "#c9a227",
              fontFamily: FONT_BODY,
            }}>{player.points}</span>
          </div>
          {(player.penalties || 0) > 0 && (
            <div style={{
              display: "flex", alignItems: "center", gap: "4px",
              padding: "3px 8px",
              background: "rgba(220, 50, 50, 0.12)",
              border: "1px solid rgba(220, 50, 50, 0.25)",
              borderRadius: "12px",
            }}>
              <span style={{ fontSize: "12px" }}>⚠</span>
              <span style={{
                fontSize: "14px", fontWeight: 700, color: "#e85555",
                fontFamily: FONT_BODY,
              }}>{player.penalties}</span>
            </div>
          )}
        </div>

        {/* Second line, full width: earned bar (FB-20, one scale for every
            row) and the Fire button, which leaves the top line to the name */}
        <div style={{
          flexBasis: "100%", paddingLeft: "40px", marginTop: "6px",
          display: "flex", alignItems: "center", gap: "12px",
          minHeight: "30px",  // same height with or without a Fire button
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <EarnedBar earned={player.totalEarned} max={maxEarned} />
          </div>
          {/* Fire button (always visible on other players' rows). Your own row
              keeps an empty slot of the same width so bars line up. */}
          {isYou || !canFire ? <span style={{ width: "78px", flexShrink: 0 }} /> : (
            <button
              onClick={(e) => { e.stopPropagation(); setShowFire(!showFire); }}
              aria-expanded={showFire}
              style={{
                width: "78px", height: "30px", padding: "0", boxSizing: "border-box",
                fontSize: "11px", fontWeight: 700,
                letterSpacing: "1px", textTransform: "uppercase",
                fontFamily: FONT_BODY,
                color: showFire ? "#e85555" : "#0a0a14",
                background: showFire ? "transparent" : "#e85555",
                border: "1.5px solid #e85555", borderRadius: "8px",
                cursor: "pointer", flexShrink: 0,
              }}
            >🏹 Fire</button>
          )}
        </div>
      </div>

      {/* Read-only roster (FB-03) */}
      {showRoster && (
        <div style={{
          padding: "10px 16px",
          background: "rgba(255,255,255,0.02)",
          border: "1.5px solid rgba(255,255,255,0.08)",
          borderTop: "none",
          borderRadius: showFire ? 0 : "0 0 10px 10px",
        }}>
          <ArrowBreakdown player={player} />
          {player.roster.length === 0 ? (
            <p style={{ fontSize: "12px", color: "#5a5a6e", margin: 0, fontFamily: FONT_BODY }}>No players drafted.</p>
          ) : (
            player.roster.map((rp) => {
              const c = getPositionColor(rp.position);
              return (
                <div key={rp.player_id} style={{
                  display: "flex", alignItems: "center", gap: "10px",
                  padding: "6px 0",
                }}>
                  <span style={{
                    display: "inline-block", padding: "2px 6px", minWidth: "30px", textAlign: "center",
                    fontSize: "9px", fontWeight: 700, letterSpacing: "1px",
                    fontFamily: FONT_BODY,
                    color: c.text, background: c.bg,
                    border: `1px solid ${c.border}`, borderRadius: "3px",
                  }}>{rp.position}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ fontSize: "13px", color: "#e8e6df", fontFamily: FONT_BODY }}>{rp.name}</span>
                    <StatChips player={rp} style={{ marginTop: "2px" }} />
                  </div>
                  <span style={{
                    fontSize: "13px", fontWeight: 700, fontFamily: FONT_BODY,
                    color: rp.points > 0 ? "#32c850" : rp.points < 0 ? "#e85555" : "#5a5a6e",
                  }}>{rp.points > 0 ? `+${rp.points}` : rp.points}</span>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Fire arrows panel */}
      {showFire && !isYou && canFire && (
        <div style={{
          padding: "16px",
          background: "rgba(220, 50, 50, 0.04)",
          border: "1.5px solid rgba(255,255,255,0.08)",
          borderTop: "none",
          borderRadius: "0 0 10px 10px",
        }}>
          <p style={{
            fontSize: "11px", fontWeight: 600, letterSpacing: "1.5px",
            textTransform: "uppercase", color: "#e85555",
            margin: "0 0 12px", fontFamily: FONT_BODY, ...ELLIPSIS,
          }}>
            Fire arrows at {player.teamName}
          </p>

          {/* FB-25: unit selector, only when House Rules turn packages on */}
          {packages.length > 0 && (
            <div role="radiogroup" aria-label="What to fire" style={{ display: "flex", gap: "6px", marginBottom: "10px" }}>
              {[{ unit: "arrow", worth: 1 }, ...packages].map((opt) => {
                const affordable = opt.worth <= myTokens;
                const selected = unit === opt.unit;
                return (
                  <button
                    key={opt.unit}
                    role="radio"
                    aria-checked={selected}
                    disabled={!affordable}
                    onClick={(e) => { e.stopPropagation(); setFireUnit(opt.unit); setSpendAmount(1); }}
                    style={{
                      flex: "1 1 0", minWidth: 0, padding: "7px 4px",
                      fontSize: "11px", fontWeight: 700, fontFamily: FONT_BODY,
                      color: selected ? "#0a0a14" : affordable ? "#e8e6df" : "#5a5a6e",
                      background: selected ? "#e85555" : "rgba(255,255,255,0.04)",
                      border: `1px solid ${selected ? "#e85555" : "rgba(255,255,255,0.1)"}`,
                      borderRadius: "8px", cursor: affordable ? "pointer" : "not-allowed",
                      ...ELLIPSIS,
                    }}
                  >
                    {opt.unit === "arrow" ? "Arrows" : UNIT_TITLES[opt.unit]}
                    {opt.unit !== "arrow" && (
                      <span style={{ display: "block", fontSize: "9px", fontWeight: 600, opacity: 0.8 }}>
                        {affordable ? `${opt.worth} arrows` : `needs ${opt.worth}`}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}

          <div style={{
            display: "flex", alignItems: "center", gap: "10px",
          }}>
            {/* Decrease */}
            <button
              onClick={(e) => { e.stopPropagation(); setSpendAmount(Math.max(1, fireCount - 1)); }}
              style={{
                width: "36px", height: "36px",
                fontSize: "18px", fontWeight: 700,
                color: "#e8e6df", background: "rgba(255,255,255,0.06)",
                border: "1px solid rgba(255,255,255,0.1)",
                borderRadius: "8px", cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}
            >−</button>

            {/* Amount */}
            <div style={{
              flex: 1, textAlign: "center",
              padding: "8px",
              background: "rgba(255,255,255,0.03)",
              border: "1px solid rgba(255,255,255,0.08)",
              borderRadius: "8px",
            }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "6px" }}>
                {unit === "arrow" ? <ArrowIcon size={16} /> : <span style={{ fontSize: "15px" }}>{unit === "meteor" ? "☄️" : "💣"}</span>}
                <span style={{
                  fontSize: "20px", fontWeight: 700, color: "#e8e6df",
                  fontFamily: FONT_BODY,
                }}>{fireCount}</span>
              </div>
            </div>

            {/* Increase */}
            <button
              onClick={(e) => { e.stopPropagation(); setSpendAmount(Math.min(maxCount, fireCount + 1)); }}
              style={{
                width: "36px", height: "36px",
                fontSize: "18px", fontWeight: 700,
                color: "#e8e6df", background: "rgba(255,255,255,0.06)",
                border: "1px solid rgba(255,255,255,0.1)",
                borderRadius: "8px", cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}
            >+</button>

            {/* Send button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                onSpend(player.username, fireCount, unit);
                setShowFire(false);
                setSpendAmount(1);
                setFireUnit("arrow");
              }}
              style={{
                padding: "8px 20px",
                fontSize: "12px", fontWeight: 700,
                letterSpacing: "1.5px", textTransform: "uppercase",
                fontFamily: FONT_BODY,
                color: "#0a0a14", background: "#e85555",
                border: "none", borderRadius: "8px",
                cursor: "pointer", transition: "all 0.2s ease",
                boxShadow: "0 2px 10px rgba(220, 50, 50, 0.3)",
              }}
            >
              Fire
            </button>
          </div>
          {/* FB-25/27 preview: what the target will owe, in real-world terms */}
          {hasHouseRules(houseRules) && (
            <p style={{
              fontSize: "12px", color: "#c0c0cc", margin: "10px 0 0",
              fontFamily: FONT_BODY, ...ELLIPSIS,
            }}>
              {player.teamName} will owe: {firePreview.what}{firePreview.serve && ` — ${firePreview.serve}`}
            </p>
          )}
          <p style={{
            fontSize: "11px", color: "#5a5a6e", margin: "6px 0 0",
            fontFamily: FONT_BODY, fontStyle: "italic",
          }}>
            This action equates to a real-life action!
          </p>
        </div>
      )}
    </div>
  );
}

// --- Your roster with live points ---
function RosterCard({ roster }) {
  if (!roster || roster.length === 0) return null;

  const totalPoints = roster.reduce((sum, p) => sum + p.points, 0);

  return (
    <div style={{
      background: "rgba(12, 12, 22, 0.9)",
      backdropFilter: "blur(20px)",
      border: "1px solid rgba(201, 162, 39, 0.15)",
      borderRadius: "14px",
      padding: "20px",
      boxShadow: "0 16px 60px rgba(0,0,0,0.5), inset 0 1px 0 rgba(201,162,39,0.08)",
      marginBottom: "12px",
    }}>
      <div style={{
        display: "flex", justifyContent: "space-between", alignItems: "center",
        marginBottom: "14px",
      }}>
        <p style={{
          fontSize: "12px", fontWeight: 700, letterSpacing: "2px",
          textTransform: "uppercase", color: "#c9a227",
          margin: 0, fontFamily: FONT_BODY,
        }}>Your Roster</p>
        <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
          <ArrowIcon size={14} />
          <span style={{
            fontSize: "14px", fontWeight: 700, color: "#c9a227",
            fontFamily: FONT_BODY,
          }}>{totalPoints} total</span>
        </div>
      </div>
      {roster.map((player, i) => {
        const c = getPositionColor(player.position);
        // Show any yardage category with yards; totals can be negative (FB-21)
        const yardCategories = [
          { label: "Pass", yards: player.total_yards_passed || 0, max: 400 },
          { label: "Rush", yards: player.total_yards_rushed || 0, max: 100 },
          { label: "Rec",  yards: player.total_yards_received || 0, max: 100 },
        ].filter(cat => cat.yards !== 0);

        return (
          <div key={i} style={{
            padding: "12px",
            background: player.scored ? "rgba(50, 200, 80, 0.06)" : "rgba(255,255,255,0.02)",
            border: `1px solid ${player.scored ? "rgba(50, 200, 80, 0.15)" : "rgba(255,255,255,0.04)"}`,
            borderRadius: "8px", marginBottom: "6px",
            transition: "all 0.3s ease",
          }}>
            {/* Top row - name and points */}
            <div style={{
              display: "flex", alignItems: "center", gap: "10px",
              marginBottom: yardCategories.length > 0 ? "8px" : 0,
            }}>
              <span style={{
                display: "inline-block", padding: "3px 6px",
                fontSize: "9px", fontWeight: 700, letterSpacing: "1px",
                fontFamily: FONT_BODY,
                color: c.text, background: c.bg,
                border: `1px solid ${c.border}`, borderRadius: "3px",
              }}>{player.position}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{
                  fontSize: "13px", fontWeight: 600, color: "#e8e6df",
                  margin: 0, fontFamily: FONT_BODY,
                }}>{player.name}</p>
                <p style={{
                  fontSize: "10px", color: "#5a5a6e", margin: "2px 0 0",
                  fontFamily: FONT_BODY,
                }}>{player.team}</p>
                <StatChips player={player} style={{ marginTop: "3px" }} />
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                <ArrowIcon size={14} />
                <span style={{
                  fontSize: "16px", fontWeight: 700,
                  color: player.points > 0 ? "#32c850" : player.points < 0 ? "#e85555" : "#5a5a6e",
                  fontFamily: FONT_BODY,
                }}>
                  {player.points > 0 ? `+${player.points}` : player.points}
                </span>
              </div>
            </div>

            {/* Yardage bars - one per category with yards */}
            {yardCategories.map((cat, idx) => (
              <div key={cat.label} style={{
                display: "flex", alignItems: "center", gap: "8px",
                padding: "6px 10px",
                background: "rgba(255,255,255,0.03)",
                borderRadius: "6px",
                marginBottom: idx < yardCategories.length - 1 ? "4px" : 0,
              }}>
                <span style={{
                  fontSize: "10px", fontWeight: 600, letterSpacing: "1px",
                  color: "#5a5a6e", fontFamily: FONT_BODY,
                  minWidth: "32px",
                }}>{cat.label}</span>
                <div style={{
                  flex: 1, height: "6px", borderRadius: "3px",
                  background: "rgba(255,255,255,0.05)",
                  overflow: "hidden",
                }}>
                  <div style={{
                    height: "100%", borderRadius: "3px",
                    background: c.text,
                    width: `${Math.min(Math.max(cat.yards / cat.max, 0) * 100, 100)}%`,
                    transition: "width 0.5s ease",
                  }}/>
                </div>
                <span style={{
                  fontSize: "11px", fontWeight: 700, color: c.text,
                  fontFamily: FONT_BODY,
                  minWidth: "45px", textAlign: "right",
                }}>{cat.yards} yds</span>
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}

// --- Yardage entries: a single play vs. a game-total milestone ---
const YARD_TYPES = [
  { category: "pass_yards", icon: "📏", color: "#c9a227", words: ["-yard pass", "passing yard"] },
  { category: "rush_yards", icon: "🏃", color: "#4a9eff", words: ["-yard run", "rushing yard"] },
  { category: "rec_yards", icon: "🧤", color: "#32c878", words: ["-yard catch", "receiving yard"] },
];

// Tag shown on entries that earned arrows
const ACCRUAL_TAGS = { touchdown: "Touchdown", field_goal: "Field Goal" };

// --- Categorize event type from awarded_plays data ---
function categorizeEvent(event) {
  const e = (event.eventType || "").toLowerCase();
  if (e.includes("touchdown")) return { category: "touchdown", icon: "🏈", color: "#c9a227" };
  if (e.includes("field goal good")) return { category: "field_goal", icon: "🎯", color: "#c9a227" };
  const yard = YARD_TYPES.find((t) => t.words.some((w) => e.includes(w)));
  if (yard) {
    // Older entries have no kind; there, only milestones carried arrows
    const isMilestone = event.kind ? event.kind === "milestone" : event.tokens > 0;
    return isMilestone
      ? { category: yard.category, icon: "🏅", color: "#c9a227", isMilestone: true }
      : { category: yard.category, icon: yard.icon, color: yard.color };
  }
  if (e.includes("interception")) return { category: "negative", icon: "🛑", color: "#e85555" };
  if (e.includes("fumble")) return { category: "negative", icon: "💥", color: "#e85555" };
  if (e.includes("incompletion")) return { category: "negative", icon: "❌", color: "#e85555" };
  if (e.includes("field goal missed")) return { category: "negative", icon: "❌", color: "#e85555" };
  if (e.includes("penalty")) return { category: "negative", icon: "🚩", color: "#e85555" };
  if (e.includes("owed")) return { category: "negative", icon: "⚠", color: "#e85555" };
  if (e.includes("spent") || e.includes("fired")) return { category: "token_spent", icon: "🏹", color: "#e85555" };
  return { category: "other", icon: "📋", color: "#7a7a8e" };
}

// --- Feed time label: game clock when known, else relative time (FB-08) ---
function feedTimeLabel(event, now) {
  if (event.period && event.clock) {
    const period = event.period > 4 ? (event.period === 5 ? "OT" : `${event.period - 4}OT`) : `Q${event.period}`;
    return `${period} ${event.clock}`;
  }
  if (!event.createdAt) return "";
  const secs = Math.max(0, Math.round((now - event.createdAt.getTime()) / 1000));
  if (secs < 60) return "just now";
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  return `${Math.floor(secs / 3600)}h ago`;
}

// --- Feed order: strictly newest first, so new lines always land on top.
// A play's own line is written before what it earned, and milestones are
// written in increasing order, so a burst stacks as: 50, 40, 30, 20, the catch.
// Ties (same timestamp) fall back to that same write order. ---
function orderFeed(entries) {
  const time = (e) => e.createdAt?.getTime() || 0;
  return [...entries].sort((a, b) =>
    time(b) - time(a) || b.playSeq - a.playSeq || b.subOrder - a.subOrder
  );
}

// --- localStorage helpers (non-fatal when storage is unavailable) ---
function readStorage(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    return raw === null ? fallback : JSON.parse(raw);
  } catch {
    return fallback;
  }
}

function writeStorage(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // storage unavailable - state just won't survive a refresh
  }
}

const HINTS_KEY = "blackowl_hints_dismissed_v1";

// --- Activity feed item ---
function ActivityItem({ event, now, houseRules }) {
  const { category, icon, color, isMilestone } = categorizeEvent(event);
  // FB-27: a fired shot reads in real-world terms, e.g.
  // "fired a CANNONBALL (10 arrows) at dana · 1 lap"
  let eventText = event.eventType;
  if (event.isSpend && event.target && (hasHouseRules(houseRules) || event.unit !== "arrow")) {
    const arrows = -event.tokens;
    const { what, serve } = describePenalty(houseRules, event.unit, event.count || arrows, arrows);
    eventText = `fired ${what} at ${event.target}${serve ? ` · ${serve}` : ""}`;
  }
  const isNegative = event.tokens < 0;
  // Every line that earned arrows gets the same gold treatment and a tag
  const isAccrual = event.tokens > 0;
  const tag = isMilestone ? "Milestone" : ACCRUAL_TAGS[category] || "Arrows";
  return (
    <div style={{
      display: "flex", alignItems: "flex-start", gap: "10px",
      padding: isAccrual ? "10px 8px" : "10px 0",
      margin: isAccrual ? "2px 0" : 0,
      background: isAccrual ? "rgba(201, 162, 39, 0.07)" : "transparent",
      border: isAccrual ? "1px solid rgba(201, 162, 39, 0.2)" : "none",
      borderBottom: isAccrual ? "1px solid rgba(201, 162, 39, 0.2)" : "1px solid rgba(255,255,255,0.04)",
      borderRadius: isAccrual ? "8px" : 0,
    }}>
      <span style={{ fontSize: "16px", flexShrink: 0 }}>
        {icon}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{
          fontSize: "13px", color: "#e8e6df", margin: 0,
          fontFamily: FONT_BODY, overflowWrap: "anywhere",
        }}>
          <span style={{ fontWeight: 600, color }}>{event.player}</span>
          {" — "}
          <span style={{ color: isAccrual ? "#e0be4a" : "#a0a0b0", fontWeight: isAccrual ? 600 : 400 }}>
            {eventText}
          </span>
          {isAccrual && (
            <span style={{
              marginLeft: "6px", padding: "1px 6px",
              fontSize: "9px", fontWeight: 700, letterSpacing: "1px",
              textTransform: "uppercase", verticalAlign: "middle",
              color: "#0a0a14", background: "#c9a227", borderRadius: "3px",
            }}>{tag}</span>
          )}
        </p>
        <p style={{
          fontSize: "10px", color: "#3a3a4e", margin: "4px 0 0",
          fontFamily: FONT_BODY, ...ELLIPSIS,
        }}>{event.user}{feedTimeLabel(event, now) && ` • ${feedTimeLabel(event, now)}`}</p>
      </div>
      {event.tokens !== 0 && (
        <div style={{ display: "flex", alignItems: "center", gap: "4px", flexShrink: 0 }}>
          <ArrowIcon size={12} />
          <span style={{
            fontSize: "13px", fontWeight: 700,
            color: isNegative ? "#e85555" : "#32c850",
            fontFamily: FONT_BODY,
          }}>
            {isNegative ? event.tokens : `+${event.tokens}`}
          </span>
        </div>
      )}
    </div>
  );
}

// --- Tab button ---
function TabButton({ label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        flex: 1, padding: "10px",
        fontSize: "11px", fontWeight: 600, letterSpacing: "1.5px",
        textTransform: "uppercase",
        fontFamily: FONT_BODY,
        color: active ? "#0a0a14" : "#5a5a6e",
        background: active ? "#c9a227" : "transparent",
        border: "none", borderRadius: "6px",
        cursor: "pointer", transition: "all 0.3s ease",
      }}
    >{label}</button>
  );
}

// --- Main Game Screen ---
export default function GameScreen({ username, lobbyId, isSpectator, onEndGame }) {
  const [tab, setTab] = useState("leaderboard");
  const [myTokens, setMyTokens] = useState(0);
  const [myPenalties, setMyPenalties] = useState(0);
  const [myPenaltiesFrom, setMyPenaltiesFrom] = useState({});
  const [alertSeq, setAlertSeq] = useState(0);
  const [lastPenalty, setLastPenalty] = useState({ from: "", amount: 0 });
  const ackKey = `blackowl_penalty_ack_${lobbyId}_${username}`;
  const [ackSeq, setAckSeq] = useState(() => readStorage(ackKey, 0));
  const [hintsDismissed, setHintsDismissed] = useState(() => readStorage(HINTS_KEY, {}));
  const welcomeKey = `blackowl_welcome_${lobbyId}_${username}`;
  const [welcomeSeen, setWelcomeSeen] = useState(() => readStorage(welcomeKey, false));
  // FB-23: when this player first reached the game screen (the idle clock's
  // start until they fire), and when they last dismissed a reminder
  const [gameSeenAt] = useState(() => {
    const key = `blackowl_game_seen_${lobbyId}_${username}`;
    const seen = readStorage(key, null) || Date.now();
    writeStorage(key, seen);
    return seen;
  });
  const reminderKey = `blackowl_reminder_${lobbyId}_${username}`;
  const [reminderDismissedAt, setReminderDismissedAt] = useState(() => readStorage(reminderKey, 0));
  const [matchesLive, setMatchesLive] = useState(true);
  // FB-15 phase 1: "(N) Black Owl" + browser notification for events that
  // land while the tab is hidden. Refs hold the last seen values so the
  // first snapshot after load never counts as new.
  const bumpMissed = useMissedEventsTitle();
  const lastEarnedRef = useRef(null);
  const lastAlertSeqRef = useRef(null);
  // FB-24 to FB-27: the lobby's House Rules (read-only here) and my unpaid
  // packaged shots. The ref lets listener callbacks see current rules.
  const [houseRules, setHouseRules] = useState({});
  const houseRulesRef = useRef({});
  const [penaltyItems, setPenaltyItems] = useState([]);
  const [paying, setPaying] = useState(false);
  const [now, setNow] = useState(Date.now());
  const [players, setPlayers] = useState([]);
  const [myRoster, setMyRoster] = useState([]);
  const [activityFeed, setActivityFeed] = useState([]);
  const [spending, setSpending] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showRules, setShowRules] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedFilter, setFeedFilter] = useState("all");

  useEffect(() => {
    // Listen for all players' points (leaderboard)
    const playersRef = collection(db, "Lobbies", lobbyId, "players");
    const unsubPlayers = onSnapshot(playersRef, (snapshot) => {
      const playerList = snapshot.docs.map((doc) => ({
        username: doc.id,
        teamName: doc.data().teamName || doc.id,
        points: doc.data().points || 0,
        penalties: doc.data().penalties || 0,
        totalEarned: doc.data().total_earned || 0,
        totalSpent: doc.data().total_spent ?? null,
        startingArrows: doc.data().starting_arrows ?? null,
        roster: Object.entries(doc.data().roster || {}).map(([playerId, info]) => ({
          player_id: playerId,
          name: info.name || "Unknown",
          position: info.position || "",
          points: info.points || 0,
          ...pickStats(info),
        })),
      }));
      setPlayers(playerList);
      const me = playerList.find(p => p.username === username);
      if (me) {
        setMyTokens(me.points);
        setMyPenalties(me.penalties);
        // FB-15: arrows earned from scoring (total_earned, FB-20)
        const prev = lastEarnedRef.current;
        if (!isSpectator && prev !== null && me.totalEarned > prev) {
          const gained = me.totalEarned - prev;
          bumpMissed();
          notify(`+${gained} arrow${gained !== 1 ? "s" : ""} earned`, "Your players just scored. Time to fire!", "earned");
        }
        lastEarnedRef.current = me.totalEarned;
      }
    });

    // Listen for penalty changes on my player doc
    const myPlayerRef = doc(db, "Lobbies", lobbyId, "players", username);
    const unsubMyPlayer = onSnapshot(myPlayerRef, (snapshot) => {
      const data = snapshot.data();
      if (data) {
        setMyPenaltiesFrom(data.penalties_from || {});
        setAlertSeq(data.penalty_alert_seq || 0);
        setLastPenalty({
          from: data.last_penalty_from || "",
          amount: data.last_penalty_amount || 0,
          unit: data.last_penalty_unit || "arrow",
          count: data.last_penalty_count || data.last_penalty_amount || 0,
        });
        // FB-15: same signal as the hit alert modal (penalty_alert_seq)
        const seq = data.penalty_alert_seq || 0;
        if (!isSpectator && lastAlertSeqRef.current !== null && seq > lastAlertSeqRef.current) {
          const from = data.last_penalty_from || "Someone";
          const amount = data.last_penalty_amount || 0;
          // FB-27 wording; The Game only fires loose arrows (D10)
          const unit = from === GAME_SENDER ? "arrow" : data.last_penalty_unit || "arrow";
          const { what, serve } = describePenalty(houseRulesRef.current, unit, data.last_penalty_count || amount, amount);
          bumpMissed();
          notify(
            `${from} hit you with ${what}`,
            serve ? `Time to serve ${serve}.` : "Open Black Owl to see what you owe.",
            "penalty"
          );
        }
        lastAlertSeqRef.current = seq;

        if (data.roster) {
          const roster = Object.entries(data.roster).map(([playerId, info]) => ({
            player_id: playerId,
            name: info.name || "Unknown",
            position: info.position || "",
            team: info.team || "",
            points: info.points || 0,
            scored: (info.points || 0) > 0,
            total_yards_passed: info.total_yards_passed || 0,
            total_yards_rushed: info.total_yards_rushed || 0,
            total_yards_received: info.total_yards_received || 0,
            ...pickStats(info),
          }));
          setMyRoster(roster);
        }
      }
    });

    // Listen for awarded plays (activity feed)
    // Newest first. Entries without created_at (pre-FB-08 test data) drop out.
    const awardsQuery = query(
      collection(db, "Lobbies", lobbyId, "awarded_plays"),
      orderBy("created_at", "desc")
    );
    const unsubAwards = onSnapshot(awardsQuery, (snapshot) => {
      const awards = snapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          player: data.player || "Unknown",
          eventType: data.event || "",
          tokens: data.points || 0,
          owed: data.owed || 0,
          kind: data.kind || "",
          subOrder: data.sub_order ?? 0,
          user: data.user || "",
          target: data.target || "",
          isSpend: data.match === "penalty",
          unit: data.unit || "arrow",
          count: data.count || 0,
          playSeq: data.play_seq || 0,
          period: data.period,
          clock: data.clock,
          createdAt: data.created_at ? data.created_at.toDate() : null,
        };
      });
      // Zero-arrow incompletions are noise (older lobbies still have them)
      const visible = awards.filter((a) => a.eventType !== "Pass Incompletion");
      setActivityFeed(orderFeed(visible));
    });

    // FB-24: House Rules are on the lobby doc
    const unsubLobby = onSnapshot(doc(db, "Lobbies", lobbyId), (snapshot) => {
      const rules = (snapshot.data() || {}).house_rules || {};
      houseRulesRef.current = rules;
      setHouseRules(rules);
    });

    // FB-26: my unpaid packaged shots, each paid whole
    const itemsQuery = query(
      collection(db, "Lobbies", lobbyId, "players", username, "penalty_items"),
      where("paid", "==", false)
    );
    const unsubItems = onSnapshot(itemsQuery, (snapshot) => {
      setPenaltyItems(snapshot.docs.map((d) => {
        const it = d.data();
        return {
          id: d.id, from: it.from, unit: it.unit, count: it.count || 1, arrows: it.arrows || 0,
          actionSnapshot: it.action_snapshot || "", pluralSnapshot: it.plural_snapshot || "",
          createdAt: it.created_at ? it.created_at.toDate().getTime() : 0,
        };
      }).sort((a, b) => a.createdAt - b.createdAt));
    });

    // FB-23: only remind while a match is still going
    const unsubMatches = onSnapshot(collection(db, "Lobbies", lobbyId, "matches"), (snapshot) => {
      setMatchesLive(snapshot.docs.length === 0 || snapshot.docs.some((d) => !d.data().completed));
    });

    // Keep relative feed times fresh (and the FB-23 idle clock ticking)
    const tick = setInterval(() => setNow(Date.now()), 30000);

    return () => {
      unsubPlayers();
      unsubMyPlayer();
      unsubAwards();
      unsubMatches();
      unsubLobby();
      unsubItems();
      clearInterval(tick);
    };
  }, [lobbyId]);

  // `count` arrows, or `count` packages of `unit` (FB-25). The server
  // computes the arrow cost from the saved House Rules; this is only a
  // pre-check so the player gets a quick answer.
  const handleSpendTokens = async (target, count, unit = "arrow") => {
    setError("");
    setSuccess("");

    const pkg = packageOptions(houseRules).find((p) => p.unit === unit);
    const amount = pkg ? pkg.worth * count : count;
    if (amount > myTokens) {
      setError(`Not enough arrows. You have ${myTokens}.`);
      return;
    }

    setSpending(true);
    try {
      const result = await apiCall("spend_tokens", {
        username: username,
        lobby_id: lobbyId,
        target: target,
        token_amount: amount,
        ...(pkg ? { package: unit, count } : {}),
      });
      // spend_tokens reports refusals in the body with a 200
      if (result.status === "error") throw new Error(result.message);
      const { what } = describePenalty(houseRules, pkg ? unit : "arrow", count, amount);
      setSuccess(`Fired ${what} at ${target}!`);
      setTimeout(() => setSuccess(""), 3000);
      // Firestore listeners will automatically update the leaderboard
    } catch (err) {
      setError(err.message || "Failed to fire arrows");
    } finally {
      setSpending(false);
    }
  };

  // FB-26: one packaged shot, paid whole
  const handlePayItem = async (itemId) => {
    setError("");
    setPaying(true);
    try {
      await apiCall("reduce_penalty", { username, lobby_id: lobbyId, item_id: itemId });
    } catch (err) {
      setError(err.message || "Failed to pay penalty");
    } finally {
      setPaying(false);
    }
  };

  // FB-26: loose arrows and every package (D8)
  const handlePayAll = async () => {
    setError("");
    setPaying(true);
    try {
      await apiCall("reduce_penalty", { username, lobby_id: lobbyId, pay_all: true });
    } catch (err) {
      setError(err.message || "Failed to pay penalty");
    } finally {
      setPaying(false);
    }
  };

  const handlePay = async (amount, fromUser) => {
    setError("");
    setPaying(true);
    try {
      await apiCall("reduce_penalty", {
        username: username,
        lobby_id: lobbyId,
        amount: amount,
        ...(fromUser ? { from_user: fromUser } : {}),
      });
    } catch (err) {
      setError(err.message || "Failed to pay penalty");
    } finally {
      setPaying(false);
    }
  };

  // --- Blocking penalty alerts (FB-10) ---
  // penalty_alert_seq counts alerts on the server; ackSeq is how many this
  // device has acknowledged. Each unacknowledged alert is matched to the
  // newest feed entries aimed at me, falling back to last_penalty_*.
  const pendingAlerts = Math.max(alertSeq - ackSeq, 0);
  const alertQueue = useMemo(() => {
    if (pendingAlerts === 0) return [];
    const hits = activityFeed
      .filter((e) => e.target === username && (e.isSpend || e.owed > 0))
      .slice(0, pendingAlerts)
      .reverse()
      .map((e) => (e.isSpend
        ? { from: e.user, amount: -e.tokens, unit: e.unit, count: e.count }
        : { from: GAME_SENDER, amount: e.owed, unit: "arrow", count: e.owed }));
    while (hits.length < pendingAlerts) hits.push({ ...lastPenalty });
    return hits;
  }, [pendingAlerts, activityFeed, username, lastPenalty]);
  const currentAlert = alertQueue[0];
  const myPlayer = players.find((p) => p.username === username);

  // FB-23 (D8): unspent arrows, no fire for 20 min, at most one per 20 min
  const lastFireAt = activityFeed.reduce(
    (latest, e) => (e.isSpend && e.user === username && e.createdAt
      ? Math.max(latest, e.createdAt.getTime()) : latest),
    gameSeenAt
  );
  const showSpendReminder = !isSpectator && matchesLive && myTokens > 0
    && now - lastFireAt >= REMINDER_IDLE_MS
    && now - reminderDismissedAt >= REMINDER_IDLE_MS;
  const dismissSpendReminder = () => {
    const at = Date.now();
    setReminderDismissedAt(at);
    writeStorage(reminderKey, at);
  };

  // Vibrate when a new alert arrives (Android Chrome; iOS Safari has no vibration API)
  const lastSeenSeq = useRef(0);
  useEffect(() => {
    if (alertSeq > lastSeenSeq.current && pendingAlerts > 0 && navigator.vibrate) {
      navigator.vibrate([200, 100, 200]);
    }
    lastSeenSeq.current = alertSeq;
  }, [alertSeq, pendingAlerts]);

  const acknowledgeAlert = () => {
    const next = ackSeq + 1;
    setAckSeq(next);
    writeStorage(ackKey, next);
  };

  const acknowledgeAllAlerts = () => {
    setAckSeq(alertSeq);
    writeStorage(ackKey, alertSeq);
  };

  const dismissHint = (key) => {
    const next = { ...hintsDismissed, [key]: true };
    setHintsDismissed(next);
    writeStorage(HINTS_KEY, next);
  };

  // Sort players by points descending
  const sortedPlayers = [...players].sort((a, b) => b.points - a.points);
  // FB-20: why the leader leads, and the scale for everyone's earned bar
  const maxEarned = Math.max(0, ...players.map((p) => p.totalEarned));
  const runnerUp = sortedPlayers[1];
  const leadGap = runnerUp ? sortedPlayers[0].points - runnerUp.points : null;
  const leadText = leadGap === null ? ""
    : leadGap > 0 ? `+${leadGap} ahead of ${runnerUp.teamName}` : `Tied with ${runnerUp.teamName}`;

  return (
    <div style={{
      minHeight: "100vh",
      display: "flex", alignItems: "flex-start", justifyContent: "center",
      fontFamily: FONT_BODY, position: "relative",
      paddingTop: "30px", paddingBottom: "40px",
    }}>
      <FieldBackground />

      <div style={{
        position: "relative", zIndex: 1,
        width: "100%", maxWidth: "500px", padding: "0 16px",
      }}>
        {/* Header */}
        <div style={{
          background: "rgba(12, 12, 22, 0.9)",
          backdropFilter: "blur(20px)",
          border: "1px solid rgba(201, 162, 39, 0.15)",
          borderRadius: "14px",
          padding: "16px 22px",
          boxShadow: "0 16px 60px rgba(0,0,0,0.5), inset 0 1px 0 rgba(201,162,39,0.08)",
          marginBottom: "12px",
        }}>
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <OwlIcon />
              <div>
                <h1 style={{
                  fontSize: "15px", fontWeight: 700, color: "#c9a227",
                  margin: 0, letterSpacing: "2px", textTransform: "uppercase",
                  fontFamily: FONT_DISPLAY,
                }}>Game Day</h1>
                <p style={{
                  fontSize: "11px", color: "#5a5a6e", margin: "2px 0 0",
                  fontFamily: FONT_BODY,
                }}>Lobby {lobbyId}</p>
              </div>
            </div>
            <button
              onClick={async () => {
                if (isSpectator || window.confirm("Leave this lobby? This cannot be undone.")) {
                  try {
                    await apiCall("leave_lobby", leaveLobbyBody(username, lobbyId, isSpectator));
                  } catch (err) {
                    // Still navigate even if API fails
                  }
                  onEndGame();
                }
              }}
              style={{
                padding: "8px 14px", fontSize: "11px",
                fontWeight: 600, letterSpacing: "1.5px",
                textTransform: "uppercase",
                fontFamily: FONT_BODY,
                color: "#e85555", background: "rgba(220, 50, 50, 0.08)",
                border: "1px solid rgba(220, 50, 50, 0.2)",
                borderRadius: "6px", cursor: "pointer",
              }}
            >Leave</button>
          </div>
        </div>

        {showSpendReminder && (
          <SpendReminder
            arrows={myTokens}
            onFire={() => { setTab("leaderboard"); dismissSpendReminder(); }}
            onDismiss={dismissSpendReminder}
          />
        )}

        {/* Arrows you owe (FB-05 / FB-06); spectators see a badge instead */}
        {isSpectator ? (
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px",
            padding: "14px 18px", marginBottom: "12px",
            background: "rgba(74, 158, 255, 0.05)",
            border: "1px solid rgba(74, 158, 255, 0.15)", borderRadius: "12px",
          }}>
            <SpectatingBadge />
            <span style={{ fontSize: "12px", color: "#7a7a8e", fontFamily: FONT_BODY, ...ELLIPSIS }}>
              Watching live. You can't fire or draft.
            </span>
          </div>
        ) : myPenalties > 0 ? (
          <OweCard
            penalties={myPenalties}
            penaltiesFrom={myPenaltiesFrom}
            items={penaltyItems}
            houseRules={houseRules}
            onPay={handlePay}
            onPayItem={handlePayItem}
            onPayAll={handlePayAll}
            paying={paying}
          />
        ) : !hintsDismissed.owe && (
          <HintCard
            text="Arrows you owe show up here, with who sent them."
            onDismiss={() => dismissHint("owe")}
          />
        )}

        {/* Arrow scoreboard */}
        {!isSpectator && <ArrowScoreboard tokens={myTokens} username={username} />}

        {/* Tabs */}
        <div style={{
          display: "flex", gap: "4px",
          marginBottom: "12px",
          background: "rgba(12, 12, 22, 0.9)",
          backdropFilter: "blur(20px)",
          borderRadius: "10px", padding: "4px",
          border: "1px solid rgba(201, 162, 39, 0.1)",
        }}>
          <TabButton label="Standings" active={tab === "leaderboard"} onClick={() => setTab("leaderboard")} />
          {!isSpectator && (
            <TabButton label="My Roster" active={tab === "roster"} onClick={() => setTab("roster")} />
          )}
          <TabButton label="Feed" active={tab === "feed"} onClick={() => setTab("feed")} />
        </div>

        {/* Success / Error */}
        {success && (
          <div style={{
            padding: "12px 16px", marginBottom: "12px",
            background: "rgba(50, 180, 80, 0.1)",
            border: "1px solid rgba(50, 180, 80, 0.25)",
            borderRadius: "8px", color: "#4ade6a",
            fontSize: "13px", fontFamily: FONT_BODY,
          }}>{success}</div>
        )}
        {error && (
          <div style={{
            padding: "12px 16px", marginBottom: "12px",
            background: "rgba(220, 50, 50, 0.1)",
            border: "1px solid rgba(220, 50, 50, 0.25)",
            borderRadius: "8px", color: "#e85555",
            fontSize: "13px", fontFamily: FONT_BODY,
          }}>{error}</div>
        )}

        {/* --- Leaderboard Tab --- */}
        {tab === "leaderboard" && (
          <div>
            {!isSpectator && !hintsDismissed.fire && players.length > 1 && (
              <HintCard
                text="Tap Fire to send arrows at a friend. Tap a row to see their roster."
                onDismiss={() => dismissHint("fire")}
              />
            )}
            {sortedPlayers.map((player, i) => (
              <LeaderboardRow
                key={player.username}
                player={player}
                rank={i + 1}
                isYou={player.username === username}
                maxEarned={maxEarned}
                leadText={i === 0 ? leadText : ""}
                canFire={!isSpectator}
                onSpend={handleSpendTokens}
                houseRules={houseRules}
                myTokens={myTokens}
              />
            ))}
          </div>
        )}

        {/* --- Roster Tab --- */}
        {tab === "roster" && (
          <RosterCard roster={myRoster} />
        )}

        {/* --- Activity Feed Tab --- */}
        {tab === "feed" && (
          <div style={{
            background: "rgba(12, 12, 22, 0.9)",
            backdropFilter: "blur(20px)",
            border: "1px solid rgba(201, 162, 39, 0.15)",
            borderRadius: "14px",
            padding: "20px",
            boxShadow: "0 16px 60px rgba(0,0,0,0.5), inset 0 1px 0 rgba(201,162,39,0.08)",
            maxHeight: "500px", overflowY: "auto",
          }}>
            <p style={{
              fontSize: "12px", fontWeight: 700, letterSpacing: "2px",
              textTransform: "uppercase", color: "#c9a227",
              margin: "0 0 12px", fontFamily: FONT_BODY,
            }}>Live Activity</p>

            {/* Feed category filters */}
            <div style={{
              display: "flex", gap: "6px", marginBottom: "12px",
              overflowX: "auto", paddingBottom: "4px",
            }}>
              {[
                { key: "all", label: "All", icon: "📋" },
                { key: "touchdown", label: "TDs", icon: "🏈" },
                { key: "yards", label: "Yards", icon: "📏" },
                { key: "negative", label: "Neg", icon: "⚠" },
                { key: "token_spent", label: "Fired", icon: "🏹" },
              ].map(({ key, label, icon }) => (
                <button
                  key={key}
                  onClick={() => setFeedFilter(key)}
                  style={{
                    padding: "6px 12px",
                    fontSize: "10px", fontWeight: 600, letterSpacing: "1px",
                    fontFamily: FONT_BODY,
                    color: feedFilter === key ? "#0a0a14" : "#5a5a6e",
                    background: feedFilter === key ? "#c9a227" : "rgba(255,255,255,0.04)",
                    border: `1px solid ${feedFilter === key ? "#c9a227" : "rgba(255,255,255,0.08)"}`,
                    borderRadius: "14px", cursor: "pointer",
                    transition: "all 0.2s ease",
                    whiteSpace: "nowrap",
                  }}
                >{icon} {label}</button>
              ))}
            </div>

            {activityFeed.length === 0 ? (
              <p style={{
                textAlign: "center", padding: "30px 0",
                color: "#5a5a6e", fontSize: "13px",
                fontFamily: FONT_BODY,
              }}>Waiting for game action...</p>
            ) : (
              activityFeed
                .filter((event) => {
                  if (feedFilter === "all") return true;
                  const cat = categorizeEvent(event).category;
                  if (feedFilter === "yards") return cat === "pass_yards" || cat === "rush_yards" || cat === "rec_yards";
                  return cat === feedFilter;
                })
                .map((event) => (
                  <ActivityItem key={event.id} event={event} now={now} houseRules={houseRules} />
                ))
            )}
          </div>
        )}

        {/* Footer */}
        <div style={{ textAlign: "center", marginTop: "16px" }}>
          {!isSpectator && <NotificationsToggle />}
          <div style={{ display: "flex", gap: "8px", justifyContent: "center", flexWrap: "wrap" }}>
            <RulesButton onClick={() => setShowRules(true)} />
            <FeedbackButton onClick={() => setShowFeedback(true)} />
          </div>
          <p style={{
            marginTop: "12px",
            fontSize: "11px", color: "#3a3a4e",
            letterSpacing: "1px", fontFamily: FONT_BODY,
          }}>
            Draft smart. Play bold. Win big.
          </p>
        </div>
      </div>

      {/* FB-18: once per user per lobby, after any pending arrow alerts */}
      {!isSpectator && !welcomeSeen && !currentAlert && myPlayer && (
        <WelcomeModal
          starterArrows={myPlayer.startingArrows ?? Math.max(players.length - 1, 0)}
          onDismiss={() => { setWelcomeSeen(true); writeStorage(welcomeKey, true); }}
        />
      )}

      {currentAlert && !isSpectator && (
        <PenaltyAlertModal
          alert={currentAlert}
          totalOwed={myPenalties}
          remaining={alertQueue.length - 1}
          onAcknowledge={acknowledgeAlert}
          onAcknowledgeAll={acknowledgeAllAlerts}
          houseRules={houseRules}
        />
      )}

      {showRules && <RulesPanel onClose={() => setShowRules(false)} houseRules={houseRules} />}
      {showFeedback && (
        <FeedbackPanel
          username={username}
          lobbyId={lobbyId}
          currentScreen="game"
          onClose={() => setShowFeedback(false)}
        />
      )}

      <style>{`
        * { box-sizing: border-box; }
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(0.85); }
        }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: rgba(201, 162, 39, 0.2); border-radius: 4px; }
      `}</style>
    </div>
  );
}
